import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "/#home" },
    { name: "Services", href: "/#services" },
    { name: "Team", href: "/#team" },
    { name: "Blog", href: "/#blog" },
    { name: "Testimonials", href: "/#testimonials" },
    { name: "Contact", href: "/#contact" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? "h-16" : "h-20"}`}>
          {/* Logo */}
          <a
            href="/#home"
            className="flex items-center gap-3 group"
            data-testid="logo"
            aria-label="BC Injury Care & Exercise (BCICE)"
          >
            <img
              src="/assets/bc-ice-logo-light-bg.png"
              alt="BC Injury Care & Exercise"
              className={`w-auto transition-all duration-300 ease-out ${
                isScrolled ? "h-8 sm:h-9" : "h-10 sm:h-11"
              }`}
              loading="eager"
              decoding="async"
            />
            <span className="hidden md:flex flex-col leading-tight border-l border-gray-200 pl-3">
              <span
                className={`font-extrabold tracking-tight text-[#0F4C3A] transition-all duration-300 ${
                  isScrolled ? "text-sm lg:text-base" : "text-base lg:text-lg"
                }`}
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                BC Injury Care <span className="text-[#C2410C]">&amp;</span> Exercise
              </span>
              <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-gray-500 mt-0.5">
                BCICE · Burnaby, BC
              </span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-medium text-gray-700 hover:text-[#0F4C3A] transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+16045534000"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#0F4C3A]"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>(604) 553-4000</span>
            </a>
            <a
              href="https://bc-ice.janeapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-appointment-btn"
            >
              <Button className="bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-6 py-2 font-semibold shadow-lg hover:shadow-xl transition-all">
                Book Appointment
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden absolute top-20 left-0 right-0 bg-white border-b border-gray-100 shadow-lg animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block text-lg font-medium text-gray-700 hover:text-[#0F4C3A] py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="https://bc-ice.janeapp.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full py-3 font-semibold">
                  Book Appointment
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
